import { ReportHeader } from "@/modules/reports/components/report-header";
import { ReportContainer } from "@/modules/reports/components/report-container";
import { Route } from "@/routes/(auth)/(headless)/reports/funds/compare";
import { format } from "date-fns";
import { Fund } from "creso-core/types";
import { useMemo } from "react";
import { useCompareFunds } from "creso-core/hooks";
import { HistoricalNavChart } from "@funds/components/funds-compare/historical-returns-chart";
import { FundScore } from "@funds/components/funds-compare/fund-score";
import { FundInfo } from "@funds/components/funds-compare/fund-info";
import { AssetAllocation } from "@funds/components/funds-compare/asset-allocation";
import { HistoricalReturns } from "@funds/components/funds-compare/historical-returns";
import { TopHoldings } from "@funds/components/funds-compare/top-holdings";
import { useWindowSize } from "usehooks-ts";
import { FundsCompareFundStrip } from "@funds/components/funds-compare/funds-compare-strip";
import { FundOverlap } from "@funds/components/funds-compare/fund-overlap";
import { REPORT_SECTIONS } from "@funds/config";
import { toBacktestOptions } from "@funds/utils/fund-compare-search-params";
import { Report } from "@/modules/reports/components";
import { ReportSection } from "@/modules/reports/components/proposal/report-section";

const ALL_SECTIONS = REPORT_SECTIONS.map((s) => s.id);

type SectionId = (typeof ALL_SECTIONS)[number];

export function FundsCompareReportPage() {
  const { distributorBranding, distributorProfile } = Route.useLoaderData();

  const search = Route.useSearch();

  const fundIds = search.funds ?? [];

  const enabledSections =
    search.sections && search.sections?.length > 0
      ? (search.sections as SectionId[])
      : (ALL_SECTIONS as unknown as SectionId[]);

  const backtestOptions = useMemo(() => toBacktestOptions(search), [search]);

  const hasSection = (id: SectionId) => enabledSections.includes(id);

  const { data } = useCompareFunds(fundIds);

  const funds = useMemo(() => {
    return data?.filter(Boolean) as Fund[];
  }, [data]);

  const allSettled = useMemo(() => {
    return data?.every((fund) => fund !== undefined);
  }, [data]);

  const { width } = useWindowSize();

  const labelWidth = useMemo(() => {
    if (width < 1024) return 150;
    let lWidth = 320;
    if (fundIds.length > 2) lWidth = 240;
    return lWidth;
  }, [width, fundIds.length]);

  if (!allSettled) return null;

  return (
    <Report embed={!!search.embed}>
      <style>{`
        @page {
          size: B4 landscape !important;
          margin: 0;
        }
      `}</style>
      <ReportHeader
        distributorBranding={distributorBranding}
        profile={distributorProfile}
      />
      <ReportContainer>
        <div className="content px-8">
          <div className="flex flex-col gap-1">
            <span className="text-2xl font-semibold tracking-tight">
              Mutual Fund Comparison Report
            </span>
            <span className="text-sm text-muted-foreground">
              Generated on {format(new Date(), "dd MMM yyyy")}
            </span>
          </div>
          <div className="flex flex-col gap-5">
            <table>
              <thead>
                <tr>
                  <td className="pb-2">
                    <FundsCompareFundStrip funds={funds} />
                  </td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="space-y-5">
                    <ReportSection
                      sectionId="returns-backtest"
                      visible={hasSection("returns-backtest")}
                    >
                      <HistoricalNavChart
                        funds={funds}
                        fundIds={fundIds}
                        labelWidth={labelWidth}
                        variant="report"
                        backtestOptions={backtestOptions}
                      />
                    </ReportSection>

                    <ReportSection
                      sectionId="fund-info"
                      visible={hasSection("fund-info")}
                    >
                      <FundInfo
                        variant="report"
                        funds={funds}
                        labelWidth={labelWidth}
                      />
                    </ReportSection>
                    <ReportSection
                      sectionId="historical-returns"
                      visible={hasSection("historical-returns")}
                    >
                      <HistoricalReturns
                        variant="report"
                        funds={funds}
                        labelWidth={labelWidth}
                      />
                    </ReportSection>
                    <ReportSection
                      sectionId="fund-score"
                      visible={hasSection("fund-score")}
                    >
                      <FundScore
                        variant="report"
                        funds={funds}
                        labelWidth={labelWidth}
                      />
                    </ReportSection>
                    <ReportSection
                      sectionId="asset-allocation"
                      visible={hasSection("asset-allocation")}
                    >
                      <AssetAllocation
                        fundIds={fundIds}
                        funds={funds}
                        labelWidth={labelWidth}
                        variant="report"
                      />
                    </ReportSection>
                    <ReportSection
                      visible={hasSection("portfolio-overlap")}
                      sectionId="portfolio-overlap"
                    >
                      <FundOverlap
                        funds={funds}
                        variant="report"
                        labelWidth={labelWidth}
                        fundIds={fundIds}
                      />
                    </ReportSection>
                    <ReportSection
                      visible={hasSection("top-holdings")}
                      sectionId="top-holdings"
                    >
                      <TopHoldings
                        variant="report"
                        fundIds={fundIds}
                        funds={funds}
                        labelWidth={labelWidth}
                      />
                    </ReportSection>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </ReportContainer>
    </Report>
  );
}
